import type { SelectOption } from "./Toolbox";
import { HomeModernIcon } from "@heroicons/react/20/solid";
import { useMemo } from "react";
import { ChecboxItem } from "./CheckboxItem";
import { ListToolButton } from "./ListToolButton";
import { Select } from "./Select";
import type { FeElementKey, MapState } from "src/types/MapState";
import { ModelVisibilityPreset } from "src/types/MapState";
import { observer } from "mobx-react-lite";
import { useTranslation } from "src/translations";

const ELEMENT_LABEL_KEYS: Record<FeElementKey, string> = {
  Unit: "presets.unit",
  Cores: "presets.cores",
  Corridors: "presets.corridors",
  Services: "presets.services",
  Amenities: "presets.amenities",
  Entrances: "presets.entrances",
  Facade: "presets.facade",
  Roof: "presets.roof",
  Parapets: "presets.parapets",
  Floors: "presets.floors",
  Ceilings: "presets.ceilings",
  Walls: "presets.walls",
  Windows: "presets.windows",
  Doors: "presets.doors",
  Structure: "presets.structure",
  Commercials: "presets.commercials",
};

const PRESET_LABEL_KEYS: Record<ModelVisibilityPreset, string> = {
  [ModelVisibilityPreset.Abstract]: "mapView.abstract",
  [ModelVisibilityPreset.Architectural]: "mapView.architectural",
  [ModelVisibilityPreset.Custom]: "mapView.custom",
};

export const BuildingPresetButton = observer(function BuildingPresetButton({
  mapState,
}: {
  mapState: MapState;
}) {
  const { t } = useTranslation();
  const presets = useMemo(() => {
    const selectOptions: SelectOption[] = mapState.modelVisibility.presetList.map((id) => ({
      id,
      label: t(PRESET_LABEL_KEYS[id]),
    }));
    return selectOptions;
  }, [mapState.modelVisibility.presetList, t]);

  const elementLabels = Object.keys(ELEMENT_LABEL_KEYS) as FeElementKey[];

  return (
    <ListToolButton Icon={HomeModernIcon} title={t("presets.title")}>
      <div className="flex flex-1 justify-center py-2 items-center space-x-2  text-gray-200">
        <HomeModernIcon className="w-4 h-4" />
        <span className="font-semibold">{t("presets.title")}</span>
      </div>
      <div className="space-y-2">
        <Select
          items={presets}
          onChange={mapState.modelVisibility.updatePreset}
          selected={mapState.modelVisibility.preset}
        />
        <div className="overflow-y-auto max-h-[600px] scrollbar-none">
          <div className="space-y-2">
            <ul className="space-y-1">
              {elementLabels.map((key) => (
                <ChecboxItem
                  key={key}
                  label={t(ELEMENT_LABEL_KEYS[key])}
                  selected={mapState.modelVisibility.elements[key]}
                  onClick={() => mapState.modelVisibility.toggle(key)}
                />
              ))}
              <ChecboxItem
                label={t("presets.floorLevels")}
                selected={mapState.floorLevels.isOn}
                onClick={mapState.floorLevels.toggle}
              />
            </ul>
          </div>
        </div>
      </div>
    </ListToolButton>
  );
});
